import { FC } from 'react';

import { IConfigIssue, IEditorChanges, IEditorPolicy, describeIssue } from '@entities/routing/config-version';
import ModalConfirm from '@features/modal-confirm';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { Tooltip } from '@mui/material';
import { useModal } from '@shared/components/Modal';

interface IProps {
  versionId: number;
  activeVersionId: number | null;
  // Diff + local validation of the working copy (local issues block Save Draft).
  changes: IEditorChanges;
  // Issues from the last save attempt — shown, never blocking (a retry re-checks them).
  serverIssues?: IConfigIssue[];
  policies: IEditorPolicy[];
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

const formatChanges = (count: number) => `${count} ${count === 1 ? 'change' : 'changes'}`;

// The edit-session strip under the bar (the design's draft banner): what's being
// edited, how much changed, and the two edit-mode actions.
const EditorBanner: FC<IProps> = ({
  versionId,
  activeVersionId,
  changes,
  serverIssues = [],
  policies,
  isSaving,
  onDiscard,
  onSave,
}) => {
  const { openModal, closeModal } = useModal();

  const saveBlocker = !changes.isDirty
    ? 'No changes to save'
    : changes.issues.length
      ? 'Fix the issues listed first'
      : '';
  const issues = [...changes.issues, ...serverIssues];

  const handleDiscard = () => {
    if (!changes.isDirty) {
      onDiscard();
      return;
    }

    openModal(
      <ModalConfirm
        title={`Discard ${formatChanges(changes.count)}?`}
        message='Your unsaved edits will be lost and the config returns to the saved version.'
        onConfirm={() => {
          closeModal();
          onDiscard();
        }}
        onCancel={closeModal}
      />,
      'sm',
    );
  };

  return (
    <section className='routing-editor-banner' aria-label='Edit session'>
      <EditNoteOutlinedIcon className='routing-editor-banner__icon' />

      <div className='routing-editor-banner__info'>
        <div className='routing-editor-banner__heading'>
          Editing <span className='routing-version-number'>v{versionId}</span>
          <span className='routing-editor-banner__count'>· {formatChanges(changes.count)}</span>
        </div>
        {changes.summary.length > 0 && (
          <div className='routing-editor-banner__summary'>{changes.summary.join(', ')}</div>
        )}
        <div className='routing-editor-banner__note'>
          {activeVersionId !== null
            ? `Routing still runs on v${activeVersionId} — nothing here is live until you publish.`
            : 'Nothing here is live until you publish.'}
        </div>
        {issues.length > 0 && (
          <ul className='routing-editor-banner__issues'>
            {issues.map(issue => (
              <li key={`${issue.path}:${issue.message}`}>{describeIssue(issue, policies)}</li>
            ))}
          </ul>
        )}
      </div>

      <div className='routing-editor-banner__actions'>
        <button
          type='button'
          className='routing-version-switcher-btn'
          onClick={handleDiscard}
          disabled={isSaving}
        >
          Discard
        </button>
        <Tooltip title={saveBlocker}>
          {/* span anchors the tooltip: button:disabled has pointer-events: none */}
          <span className='routing-config-action-wrap'>
            <button
              type='button'
              className='routing-config-action-btn'
              onClick={onSave}
              disabled={isSaving || saveBlocker !== ''}
            >
              {isSaving ? 'Saving…' : 'Save Draft'}
            </button>
          </span>
        </Tooltip>
      </div>
    </section>
  );
};

export default EditorBanner;
